import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openDocumentTab } from '../../store/tabsStore';
import { preloadLexicalEditor } from '../ui/LexicalRichEditorLazy';
import GroupHeader from './GroupHeader';
import { PageGlyph } from './treeIcons';
import { shortAge } from './shortAge';

const COLLAPSE_KEY = 'documents-pinned-collapsed';

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * "PINNED" above "ALL DOCUMENTS" (card 1.4): the pinned pages of this tree in
 * the user's order, each with how long ago it was edited ("4 d"). Collapsible
 * (remembered per device), hidden while empty, reordered by dragging within
 * the section. Pin / Unpin live in the row menu (card 3.3).
 */
export default function PinnedSection({ pins, nodeById, scope, activeDocumentTabId, openRowMenu, now = Date.now() }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  // Roving tabindex (card 6.3): one pinned row is tabbable, arrows move.
  const [focusId, setFocusId] = useState(null);
  const rows = pins.ids.map((id) => nodeById.get(id)).filter(Boolean);
  if (!rows.length) return null;

  const toggle = () => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, prev ? '0' : '1');
      } catch {
        /* storage unavailable */
      }
      return !prev;
    });
  };

  const dropOn = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const order = pins.ids.filter((id) => id !== dragId);
    order.splice(order.indexOf(targetId), 0, dragId);
    pins.reorder(order);
  };

  const tabbableId = rows.some((n) => String(n.id) === String(focusId)) ? String(focusId) : String(rows[0].id);

  /** Keys on a focused pinned row: Up / Down / Home / End move, Enter opens, Shift+F10 the row menu. */
  const onRowKeyDown = (e, node) => {
    if (e.target !== e.currentTarget) return;
    if ((e.shiftKey && e.key === 'F10') || e.key === 'ContextMenu') {
      e.preventDefault();
      openRowMenu(e, node, e.currentTarget);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.click();
      return;
    }
    const all = Array.from(e.currentTarget.parentElement.querySelectorAll('[data-pinned-id]'));
    const at = all.indexOf(e.currentTarget);
    const next = { ArrowDown: all[at + 1], ArrowUp: all[at - 1], Home: all[0], End: all[all.length - 1] }[e.key];
    if (!next) return;
    e.preventDefault();
    setFocusId(next.getAttribute('data-pinned-id'));
    next.focus();
  };

  return (
    <div className="doc-group">
      <GroupHeader label={t('docSidebar.pinned')} collapsed={collapsed} onToggle={toggle} />
      {!collapsed && (
        <div role="tree" aria-label={t('docSidebar.pinned')}>
          {rows.map((node) => {
            const active = activeDocumentTabId === `document-editor:${node.id}`;
            const at = pins.ages.get(String(node.id));
            return (
              <div
                key={node.id}
                role="treeitem"
                aria-level={1}
                aria-selected={active}
                aria-current={active ? 'page' : undefined}
                tabIndex={String(node.id) === tabbableId ? 0 : -1}
                data-pinned-id={node.id}
                className={`doc-tree-row doc-pinned-row${active ? ' doc-tree-row--active' : ''}${overId === node.id ? ' doc-tree-row--drop-before' : ''}`}
                style={{ paddingLeft: 6 }}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  try {
                    e.dataTransfer.setData('application/x-apiboo-pin', String(node.id));
                  } catch {
                    /* jsdom */
                  }
                  setDragId(node.id);
                }}
                onDragOver={(e) => {
                  if (!dragId) return;
                  e.preventDefault();
                  e.stopPropagation();
                  setOverId(node.id);
                }}
                onDragLeave={() => setOverId((prev) => (prev === node.id ? null : prev))}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  dropOn(node.id);
                  setDragId(null);
                  setOverId(null);
                }}
                onDragEnd={() => {
                  setDragId(null);
                  setOverId(null);
                }}
                onMouseEnter={preloadLexicalEditor}
                onClick={() => openDocumentTab({ documentId: node.id, label: node.name || t('docSidebar.untitled'), scope })}
                onContextMenu={(e) => openRowMenu(e, node)}
                onKeyDown={(e) => onRowKeyDown(e, node)}
                onFocus={(e) => e.target === e.currentTarget && setFocusId(String(node.id))}
              >
                <span className="doc-tree-spacer" aria-hidden />
                <PageGlyph size={16} className="doc-tree-icon" />
                <span className={`doc-tree-label${node.name ? '' : ' doc-tree-label--empty'}`}>
                  {node.name || t('docSidebar.untitled')}
                </span>
                {at ? <span className="doc-tree-meta doc-tree-meta--age">{shortAge(at, now, t)}</span> : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
